import { Injectable } from '@angular/core';
import {
    NavigationEnd,
    Router
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_LANG, SupportedLang, detectBrowserLang, isEnglishOnlyPath, normalizeLang, readLangFromUrl } from './lang.util';

@Injectable({ providedIn: 'root' })
export class LangResolverService {
    constructor(private translate: TranslateService, private router: Router) {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.applyLangFromRoute();
            }
        });
    }

    // Lingua suggerita dal contesto per le rotte senza prefisso di lingua: la
    // valorizza chi conosce il destinatario della pagina (per /my-quotes è la
    // lingua del lead, impostata da CustomerAreaService.loadCustomerArea).
    // Vale meno di un ?lang esplicito, più della lingua del browser.
    private contextLang: SupportedLang | null = null;

    setContextLang(lang: SupportedLang | null): void {
        this.contextLang = lang;

        // Applicazione immediata: serve al percorso post-OTP, dove non avviene
        // nessuna nuova navigazione (goToMyQuotes chiama loadCustomerArea senza
        // router.navigate) e quindi nessun NavigationEnd applicherebbe mai la
        // lingua. Un `?lang` esplicito vince sempre e un control panel non va
        // mai toccato.
        //
        // Nel percorso del resolver questa condizione È vera lo stesso (su un
        // caricamento fresco router.url vale '/'), ma non produce né flash né
        // doppia applicazione perché: il valore applicato è lo stesso che
        // NavigationEnd applicherebbe subito dopo, use() è idempotente, e a quel
        // punto non è ancora renderizzato nulla. L'invariante che regge è che in
        // /my-quotes si entra SEMPRE con un caricamento fresco dallo short link:
        // se un domani qualcuno ci navigasse dall'interno della SPA partendo da
        // un'altra rotta senza prefisso, la vecchia pagina resterebbe visibile e
        // cambierebbe lingua durante le chiamate HTTP. In quel caso questo guard
        // va reso esplicito sulla rotta di destinazione.
        if (lang && this.isUnprefixedRouteWithoutExplicitLang()) {
            this.translate.use(lang);
        }
    }

    private isUnprefixedRouteWithoutExplicitLang(): boolean {
        const url = this.router.url;
        const segments = url.split('/');
        const firstSegment = segments[1]?.split('?')[0] ?? '';

        if (isEnglishOnlyPath(url)) {
            return false;
        }

        if (normalizeLang(firstSegment)) {
            return false;
        }

        const search = url.includes('?') ? url.slice(url.indexOf('?')) : '';
        if (readLangFromUrl(search)) {
            return false;
        }

        return true;
    }

    private applyLangFromRoute() {
        const url = this.router.url;
        const segments = url.split('/');
        const firstSegment = segments[1]?.split('?')[0] ?? '';

        this.translate.setDefaultLang(DEFAULT_LANG);

        // Aree control panel (admin/partner/support): sempre in inglese, mai i18n
        // della lingua pubblica/browser.
        if (isEnglishOnlyPath(url)) {
            this.translate.use('en');
            return;
        }

        const routeLang = normalizeLang(firstSegment);
        if (routeLang) {
            this.translate.use(routeLang);
            return;
        }

        // Rotte senza prefisso di lingua (es. /my-quotes, aperta da uno short
        // link non modificabile che non può portare la lingua nel path).
        const search = url.includes('?') ? url.slice(url.indexOf('?')) : '';
        const langToUse = readLangFromUrl(search) ?? this.contextLang ?? detectBrowserLang();
        this.translate.use(langToUse);
    }
}